import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Save, AlertTriangle, CheckCircle2, FileText, Loader2 } from "lucide-react";
import { API, INK, MUTE, GREEN, inp, lab, btn, Badge, errMsg } from "./common";

const GROUPS = [
  ["Identity", [["consumer_name", "Consumer name", "text"], ["consumer_no", "Consumer / account no.", "text"], ["discom", "DISCOM", "text"], ["state", "State", "text"],
    ["tariff_category", "Tariff category (lookup key)", "text"], ["tariff_label", "Tariff as printed", "text"], ["voltage_kv", "Supply voltage (kV)", "number"], ["contract_demand_kva", "Contract demand (kVA)", "number"], ["sanctioned_load_kw", "Sanctioned load (kW)", "number"]]],
  ["Period", [["bill_month", "Bill month (YYYY-MM)", "text"], ["bill_date", "Bill date", "text"], ["due_date", "Due date", "text"], ["period_from", "Period from", "text"], ["period_to", "Period to", "text"], ["days", "Days", "number"]]],
  ["Energy & demand", [["kwh", "kWh", "number"], ["kvah", "kVAh", "number"], ["kvarh_lag", "kVArh lag", "number"], ["kvarh_lead", "kVArh lead", "number"], ["md_kva", "Max demand (kVA)", "number"], ["md_kw", "MDI (kW) — DHBVN", "number"],
    ["billed_demand_kva", "Billed demand (kVA)", "number"], ["pf", "Power factor", "number"], ["load_factor", "Load factor", "number"], ["mf", "Multiplying factor", "number"]]],
  ["Solar / net metering", [["has_solar", "Net-metered solar", "bool"], ["import_kwh", "Import kWh", "number"], ["export_kwh", "Export kWh", "number"], ["solar_gen_kwh", "Solar generation kWh", "number"], ["net_kwh", "Net billed kWh", "number"], ["banked_kwh", "Banked kWh", "number"]]],
  ["Charges (₹)", [["fixed_charge", "Fixed / demand charge", "number"], ["energy_charge", "Energy charge", "number"], ["energy_rate", "Energy rate (₹/unit)", "number"], ["fuel_surcharge", "FPPAS / FAC / FPPPA", "number"],
    ["tod_charge", "ToD charge (net)", "number"], ["wheeling_charge", "Wheeling charge", "number"], ["pf_adjustment", "PF penalty / incentive", "number"], ["lf_incentive", "Load-factor incentive", "number"],
    ["excess_demand_charge", "Excess demand charge", "number"], ["plec_charge", "PLEC", "number"], ["electricity_duty", "Electricity duty", "number"], ["tax_on_sale", "Tax on sale", "number"], ["municipal_tax", "Municipal tax", "number"],
    ["other_charges", "Other charges", "number"], ["arrears", "Arrears", "number"], ["current_bill", "Current bill", "number"], ["net_payable", "Net payable", "number"], ["late_payment_surcharge", "Late payment surcharge", "number"]]],
];

const CORE = ["consumer_no", "bill_month", "kwh", "kvah", "md_kva", "contract_demand_kva", "energy_charge", "fixed_charge", "net_payable"];

/** Side-by-side review: extracted fields (editable) against the bill PDF. Saving writes `data` and marks the bill confirmed. */
export default function BillReviewModal({ bill, onClose, onSaved, canEdit }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("pdf");
  const [text, setText] = useState(null);

  useEffect(() => {
    const d = bill?.data || {};
    const f = {};
    GROUPS.forEach(([, fields]) => fields.forEach(([k]) => { f[k] = d[k] ?? ""; }));
    setForm(f);
    setText(null);
    setTab("pdf");
  }, [bill]);

  useEffect(() => {
    if (tab !== "text" || text !== null || !bill) return;
    axios.get(`${API}/bills/${bill.id}/text`).then((r) => setText(r.data.text || "")).catch(() => setText(""));
  }, [tab, text, bill]);

  const missingCore = useMemo(() => CORE.filter((k) => form[k] === "" || form[k] === null || form[k] === undefined), [form]);
  const changed = (k) => String(form[k] ?? "") !== String(bill?.extracted?.[k] ?? "");

  const save = async () => {
    setBusy(true);
    try {
      const payload = { confirmed: true };
      GROUPS.forEach(([, fields]) => fields.forEach(([k, , type]) => {
        const v = form[k];
        payload[k] = type === "bool" ? !!v : (v === "" ? null : v);
      }));
      const { data } = await axios.put(`${API}/bills/${bill.id}`, payload);
      toast.success("Bill confirmed");
      onSaved?.(data);
    } catch (e) {
      toast.error(errMsg(e, "Could not save"));
    } finally { setBusy(false); }
  };

  if (!bill) return null;
  const conf = Math.round((bill.confidence ?? 0) * 100);
  const confColor = conf >= 90 ? "#166534" : conf >= 60 ? "#92400E" : "#B91C1C";

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(10,17,40,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="bill-review-modal" style={{ width: "min(1240px, 100%)", height: "min(860px, 100%)", background: "#fff", borderRadius: 16, display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 24px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderBottom: "1px solid #E2E8F0" }}>
          <FileText size={18} style={{ color: GREEN }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: INK }}>Review bill · {bill.bill_month || "month unknown"}</div>
            <div style={{ fontSize: 11, color: MUTE, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{bill.filename}{bill.pages?.length ? ` · page${bill.pages.length > 1 ? "s" : ""} ${bill.pages[0]}${bill.pages.length > 1 ? `–${bill.pages[bill.pages.length - 1]}` : ""}` : ""} · read by {bill.source === "llm" ? "AI" : bill.source === "ocr+parser" ? "OCR + parser" : "parser"}</div>
          </div>
          <Badge bg={conf >= 90 ? "#DCFCE7" : conf >= 60 ? "#FEF3C7" : "#FEE2E2"} fg={confColor} data-testid="bill-confidence">{conf}% confidence</Badge>
          {bill.confirmed ? <Badge bg="#DCFCE7" fg="#166534"><CheckCircle2 size={11} />Confirmed</Badge> : <Badge bg="#F1F5F9" fg="#475569">Unreviewed</Badge>}
          <button onClick={onClose} style={{ ...btn("ghost"), padding: "6px 8px" }} aria-label="Close"><X size={16} /></button>
        </div>

        <div style={{ flex: 1, display: "grid", gridTemplateColumns: "minmax(380px, 46%) 1fr", minHeight: 0 }}>
          {/* fields */}
          <div style={{ overflowY: "auto", padding: 16, borderRight: "1px solid #E2E8F0" }}>
            {(bill.warnings || []).length > 0 && (
              <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 10, padding: "10px 12px", marginBottom: 12 }}>
                {(bill.warnings || []).map((w, i) => <div key={i} style={{ display: "flex", gap: 8, fontSize: 12, color: "#92400E", alignItems: "flex-start" }}><AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />{w}</div>)}
              </div>
            )}
            {missingCore.length > 0 && (
              <div style={{ fontSize: 11.5, color: "#B91C1C", marginBottom: 10 }}>Missing: {missingCore.join(", ")} — fill these for a complete audit.</div>
            )}
            {GROUPS.map(([title, fields]) => (
              <div key={title} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: MUTE, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>{title}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {fields.map(([k, label, type]) => (
                    <div key={k}>
                      <label style={{ ...lab, color: CORE.includes(k) ? INK : MUTE }}>{label}{CORE.includes(k) ? " *" : ""}</label>
                      {type === "bool" ? (
                        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, padding: "6px 0" }}>
                          <input type="checkbox" checked={!!form[k]} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.checked }))} /> yes
                        </label>
                      ) : (
                        <input data-testid={`bill-field-${k}`} type={type} step="any" value={form[k] ?? ""} disabled={!canEdit}
                          onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))}
                          style={{ ...inp, borderColor: changed(k) ? "#F59E0B" : (CORE.includes(k) && form[k] === "" ? "#FCA5A5" : "#CBD5E1"), background: changed(k) ? "#FFFBEB" : "#fff" }} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* bill */}
          <div style={{ display: "flex", flexDirection: "column", minHeight: 0, background: "#F8FAFC" }}>
            <div style={{ display: "flex", gap: 6, padding: "8px 12px", borderBottom: "1px solid #E2E8F0" }}>
              {[["pdf", "Bill PDF"], ["text", "Extracted text"]].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} style={{ ...btn(tab === k ? "dark" : "ghost"), padding: "5px 10px" }}>{l}</button>
              ))}
              <a href={`${API}/bills/${bill.id}/file`} target="_blank" rel="noreferrer" style={{ ...btn("ghost"), padding: "5px 10px", marginLeft: "auto", textDecoration: "none" }}>Open in new tab</a>
            </div>
            {tab === "pdf" ? (
              <iframe title="bill" src={`${API}/bills/${bill.id}/file#page=${bill.pages?.[0] || 1}`} style={{ flex: 1, border: "none", width: "100%" }} />
            ) : (
              <pre style={{ flex: 1, overflow: "auto", margin: 0, padding: 12, fontSize: 10.5, lineHeight: 1.35, fontFamily: "ui-monospace, Menlo, monospace", color: INK, whiteSpace: "pre" }}>
                {text === null ? <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}><Loader2 size={12} className="animate-spin" />loading…</span> : (text || "(no text layer — scanned bill)")}
              </pre>
            )}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", borderTop: "1px solid #E2E8F0", background: "#fff" }}>
          <div style={{ fontSize: 11, color: MUTE, flex: 1 }}>Amber fields differ from what the parser read. The original extraction is kept, so you can always compare.</div>
          <button onClick={onClose} style={btn("ghost")}>Close</button>
          {canEdit && <button onClick={save} disabled={busy} style={btn("primary")} data-testid="bill-review-save">{busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}Save & confirm</button>}
        </div>
      </div>
    </div>
  );
}
